import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { useFeatureFlagEnabled } from '@/lib/posthog/hooks/useFeatureFlagEnabled';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { roles } from '@/modules/auth/roles';
import { useCompaniesQuery } from '@/modules/companies/queries/useCompaniesQuery';
import { Form } from '@/modules/forms/components/Form';
import { FormCombobox } from '@/modules/forms/components/FormCombobox';
import { FormInput } from '@/modules/forms/components/FormInput';
import { FormMultiSelect } from '@/modules/forms/components/FormMultiSelect';
import { FormRadioGroup } from '@/modules/forms/components/FormRadioGroup';
import { FormSelect } from '@/modules/forms/components/FormSelect';
import { FormSwitch } from '@/modules/forms/components/FormSwitch';
import { FormTextarea } from '@/modules/forms/components/FormTextarea';
import { jobStateOptions } from '@/modules/jobs/jobStates';
import { useJobSkillsMutation } from '@/modules/jobs/mutations/useJobSkillsMutation';
import type { JobDetailApi } from '@/modules/jobs/queries/useJobQuery';
import { isSourcingStrategyTransitionAllowed } from '@/modules/jobs/sourcingStrategyTransitions';
import type { SourcingStrategyApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { useUsersQuery } from '@/modules/users/queries/useUsersQuery';
import type { UserApi } from '@/modules/users/types';
import type { components } from '@/types/apiSchema';
import { isDevEnv, isLocalEnv } from '@/utils/env';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { SkillsSection } from './SkillsSection';
import { mapUsersToSelectOptions } from './userSelectOptions';
import { UsersMultiSelect } from './UsersMultiSelect';

const booleanSchema = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true');

const jobLocationTypeSchema = z.enum(['Onsite', 'Hybrid', 'Remote']);
const jobContractTypeSchema = z.enum(['EmploymentContract', 'B2B']);

const locationTypeOptions = [
  { value: 'Onsite', label: 'Onsite' },
  { value: 'Hybrid', label: 'Hybrid' },
  { value: 'Remote', label: 'Remote' },
];

const contractTypeOptions = [
  { value: 'EmploymentContract', label: 'Employment Contract' },
  { value: 'B2B', label: 'B2B' },
];

const jobStateSchema = z.enum(
  jobStateOptions.map((option) => option.value) as [
    (typeof jobStateOptions)[number]['value'],
    ...(typeof jobStateOptions)[number]['value'][],
  ],
);

const jobFormSchema = z.object({
  title: z.string().min(1, 'Title required'),
  description: z.string().min(1, 'Description required'),
  location: z.string().min(1, 'Location required'),
  locationTypes: z
    .array(jobLocationTypeSchema)
    .min(1, 'Work arrangement required'),
  contractTypes: z.array(jobContractTypeSchema).min(1, 'Contract required'),
  companyId: z.string(),
  ownerId: z.string().min(1, 'Owner required'),
  mustHaveSkills: z
    .array(z.string())
    .min(1, 'At least one must-have skill is required')
    .nullish(),
  niceToHaveSkills: z.array(z.string()).nullish(),
  initialReachOutMessageTemplate: z.string().optional(),
  isMessagingAutomated: booleanSchema,
  isMessagingCreative: booleanSchema,
  isReachOutAutomated: booleanSchema,
  isShortlistingAutomated: booleanSchema,
  isInvitationFollowUpEnabled: booleanSchema,
  isPrescreeningEnabled: booleanSchema,
  isTrackingCoordinationEvents: booleanSchema,
  isCoordinationActionAutoApproved: booleanSchema,
  coordinationProcessSettings: z
    .object({
      recruiterId: z.string().optional(),
      hiringManagerId: z.string().optional(),
    })
    .optional(),
  sourcingStrategy: z.enum(['Worker', 'Agent', 'Scout']),
  userIds: z.array(z.string()).optional(),
  state: jobStateSchema.optional(),
  isPublicCandidateListEnabled: z.boolean().optional(),
});

const createJobFormSchema = (
  originalSourcingStrategy: SourcingStrategyApi | null | undefined,
  isCoordinationAgentSettingsEnabled: boolean,
) =>
  jobFormSchema
    .superRefine((values, context) => {
      if (
        isCoordinationAgentSettingsEnabled &&
        values.isTrackingCoordinationEvents &&
        !values.coordinationProcessSettings?.recruiterId
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['coordinationProcessSettings', 'recruiterId'],
          message: 'Coordination recruiter required',
        });
      }

      if (
        isCoordinationAgentSettingsEnabled &&
        values.isTrackingCoordinationEvents &&
        !values.coordinationProcessSettings?.hiringManagerId
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['coordinationProcessSettings', 'hiringManagerId'],
          message: 'Coordination hiring manager required',
        });
      }

      if (
        originalSourcingStrategy &&
        !isSourcingStrategyTransitionAllowed(
          originalSourcingStrategy,
          values.sourcingStrategy,
        )
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sourcingStrategy'],
          message:
            'Existing legacy or Scout jobs can switch only to Sourcing Agent and cannot switch back.',
        });
      }
    })
    .transform((values) => {
      const { recruiterId, hiringManagerId } =
        values.coordinationProcessSettings ?? {};

      return {
        ...values,
        coordinationProcessSettings:
          values.isTrackingCoordinationEvents && recruiterId && hiringManagerId
            ? { recruiterId, hiringManagerId }
            : undefined,
      };
    });

type JobFormSchema = ReturnType<typeof createJobFormSchema>;

export type JobFormValues = z.input<JobFormSchema>;

export type JobFormSubmitValues = Omit<z.infer<JobFormSchema>, 'companyId'> & {
  companyId: string;
  state?: Exclude<components['schemas']['JobState'], null>;
};

interface JobFormProps {
  defaultValues?: Partial<JobDetailApi>;
  mode: 'create' | 'edit';
  submitText?: string;
  loading?: boolean;
  // TODO: this is anti-pattern, we should separate the Outreach Agent Settings from the Job Form
  showOutreachAgentSettings?: boolean;
  showSourcingStrategy?: boolean;
  hasCoordinationProcess?: boolean;
  onSubmit: (values: JobFormSubmitValues) => void;
  onCancel?: () => void;
}

// Only `DuplicateJob` (src/pages/jobs/DuplicateJob.tsx) still renders this form.
// Jobs are created and edited in JobSignalPanel (recruiter) and HMJobEditor (portal).
export const JobForm = ({
  defaultValues,
  mode,
  submitText,
  loading,
  showOutreachAgentSettings = true,
  showSourcingStrategy = true,
  hasCoordinationProcess = false,
  onSubmit,
  onCancel,
}: JobFormProps) => {
  const [mustHaveSkills, setMustHaveSkills] = useState<string[]>(
    () => defaultValues?.mustHaveSkills || [],
  );
  const [niceToHaveSkills, setNiceToHaveSkills] = useState<string[]>(
    () => defaultValues?.niceToHaveSkills || [],
  );

  const companiesQuery = useCompaniesQuery();
  const usersQuery = useUsersQuery();
  const hasUserMultipleTenants =
    companiesQuery.data?.length && companiesQuery.data.length > 1;
  const singleTenantCompanyId =
    companiesQuery.data?.length === 1 ? companiesQuery.data[0]?.id : undefined;

  const jobSkillsMutation = useJobSkillsMutation();
  const isCoordinationAgentSettingsEnabled =
    useFeatureFlagEnabled('newCoordinationAgentSettings') === true;

  const isCreateMode = mode === 'create';
  const originalSourcingStrategy = isCreateMode
    ? undefined
    : defaultValues?.sourcingStrategy;
  const sourcingStrategyOptions = [
    // Worker and Scout are removed, kept selectable only so legacy jobs can be opened and switched to Sourcing Agent.
    ...(originalSourcingStrategy === 'Worker'
      ? [{ value: 'Worker' as const, label: 'Classic (legacy)' }]
      : []),
    ...(originalSourcingStrategy === 'Scout'
      ? [{ value: 'Scout' as const, label: 'Scout (legacy)' }]
      : []),
    { value: 'Agent' as const, label: 'Sourcing Agent' },
  ].map((option) => ({
    ...option,
    disabled:
      !!originalSourcingStrategy &&
      !isSourcingStrategyTransitionAllowed(
        originalSourcingStrategy,
        option.value,
      ),
  }));

  const handleGenerateSkills = async (title: string, description: string) => {
    if (!title || !description) {
      toast.warning('Please enter a job title and description first');
      return;
    }

    jobSkillsMutation.mutate(
      { title, description },
      {
        onSuccess: (result) => {
          const generatedMustHave = result?.mustHaveSkills || [];
          const generatedNiceToHave = result?.niceToHaveSkills || [];

          const newMustHave = [
            ...new Set([...mustHaveSkills, ...generatedMustHave]),
          ];

          const newNiceToHave = [
            ...new Set([...niceToHaveSkills, ...generatedNiceToHave]),
          ];

          setMustHaveSkills(newMustHave);
          setNiceToHaveSkills(newNiceToHave);

          toast.success('Skills have been generated');
        },
        onError: (error) => {
          toast.error(
            `Failed to generate skills: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleAddSkill = (skill: string, type: 'mustHave' | 'niceToHave') => {
    const trimmedSkill = skill.trim();
    if (!trimmedSkill) return;

    if (type === 'mustHave') {
      if (!mustHaveSkills.includes(trimmedSkill)) {
        const newSkills = [...mustHaveSkills, trimmedSkill];
        setMustHaveSkills(newSkills);
        setNiceToHaveSkills((prev) => prev.filter((s) => s !== trimmedSkill));
      }
    } else {
      if (!niceToHaveSkills.includes(trimmedSkill)) {
        setNiceToHaveSkills((prev) => [...prev, trimmedSkill]);
        setMustHaveSkills((prev) => prev.filter((s) => s !== trimmedSkill));
      }
    }
  };

  const handleRemoveSkill = (
    skill: string,
    type: 'mustHave' | 'niceToHave',
  ) => {
    if (type === 'mustHave') {
      const newSkills = mustHaveSkills.filter((s) => s !== skill);
      setMustHaveSkills(newSkills);
    } else {
      setNiceToHaveSkills((prev) => prev.filter((s) => s !== skill));
    }
  };

  const handleFormSubmit = (values: z.infer<JobFormSchema>) => {
    const formValues: JobFormSubmitValues = {
      ...values,
      ...(!hasUserMultipleTenants && {
        companyId: companiesQuery.data?.[0]?.id,
      }),
      locationTypes: values.locationTypes || [],
      contractTypes: values.contractTypes || [],
      mustHaveSkills,
      niceToHaveSkills,
      state: values.state,
    };

    onSubmit(formValues);
  };

  const companiesOptions = (companiesQuery.data || [])
    .filter(
      (company): company is typeof company & { name: string } => !!company.name,
    )
    .map((company) => ({
      value: company.id,
      label: company.name,
    }));

  const usersOptions = mapUsersToSelectOptions(usersQuery.data || []);
  const ownerOptions = usersOptions;

  const defaultFormValues = {
    title: defaultValues?.title || '',
    description: defaultValues?.description || '',
    location: defaultValues?.location || '',
    locationTypes: defaultValues?.locationTypes || [],
    contractTypes: defaultValues?.contractTypes || [],
    companyId: defaultValues?.companyId || '',
    ownerId: defaultValues?.ownerId || '',
    mustHaveSkills: defaultValues?.mustHaveSkills || [],
    niceToHaveSkills: defaultValues?.niceToHaveSkills || [],
    initialReachOutMessageTemplate:
      defaultValues?.initialReachOutMessageTemplate || '',
    isMessagingAutomated: convertToString(defaultValues?.isMessagingAutomated),
    isMessagingCreative: convertToString(defaultValues?.isMessagingCreative),
    isReachOutAutomated: convertToString(defaultValues?.isReachOutAutomated),
    isShortlistingAutomated: convertToString(
      defaultValues?.isShortlistingAutomated,
    ),
    isInvitationFollowUpEnabled: convertToString(
      defaultValues?.isInvitationFollowUpEnabled,
      true,
    ),
    isPrescreeningEnabled: convertToString(
      defaultValues?.isPrescreeningEnabled,
    ),
    isTrackingCoordinationEvents: isCreateMode
      ? 'false'
      : convertToString(defaultValues?.isTrackingCoordinationEvents),
    isCoordinationActionAutoApproved: isCreateMode
      ? 'false'
      : convertToString(defaultValues?.isCoordinationActionAutoApproved),
    sourcingStrategy: isCreateMode
      ? 'Agent'
      : defaultValues?.sourcingStrategy || 'Agent',
    userIds: (defaultValues?.users || []).map((user) => user.id),
    coordinationProcessSettings:
      defaultValues?.coordinationProcessSettings ?? undefined,
    state: defaultValues?.state ?? undefined,
    ...(isCreateMode ? { isPublicCandidateListEnabled: false } : {}),
  };

  return (
    <Form
      defaultValues={defaultFormValues}
      schema={createJobFormSchema(
        originalSourcingStrategy,
        isCoordinationAgentSettingsEnabled,
      )}
      onSubmit={handleFormSubmit}
    >
      <SingleTenantCompanyIdSync companyId={singleTenantCompanyId} />

      <div className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Job details</CardTitle>
            <CardDescription>
              Provide the essential information about the position
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-8">
            <FormInput
              name="title"
              label="Title"
              placeholder="React Developer"
              required
            />

            <FormTextarea
              name="description"
              label="Description"
              placeholder="We are looking for a React Developer with 3+ years of experience..."
              rows={12}
              required
            />

            <SkillsSection
              mustHaveSkills={mustHaveSkills}
              niceToHaveSkills={niceToHaveSkills}
              onAddSkill={handleAddSkill}
              onRemoveSkill={handleRemoveSkill}
              onGenerateSkills={handleGenerateSkills}
              isGenerating={jobSkillsMutation.isPending}
            />

            <FormInput
              name="location"
              label="Location"
              placeholder="San Francisco"
              required
            />

            <FormMultiSelect
              name="locationTypes"
              label="Work arrangement"
              options={locationTypeOptions}
              placeholder="Select location types"
              required
            />

            <FormMultiSelect
              name="contractTypes"
              label="Contract"
              options={contractTypeOptions}
              placeholder="Select contract types"
              required
            />

            {!isCreateMode && (
              <FormSelect
                name="state"
                label="Job state"
                description="Published jobs are visible to hiring managers. Unpublished jobs are not visible to hiring managers. On Hold, Hired and Closed
                  jobs have muted Slack notifications."
                options={jobStateOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                placeholder="Select job state"
              />
            )}

            {hasUserMultipleTenants && (
              <PermissionsGate permissions={[permissions.Companies.Read]}>
                <FormCombobox
                  name="companyId"
                  label="Company"
                  placeholder="Select a company"
                  searchPlaceholder="Search company..."
                  emptyMessage="No company found."
                  options={companiesOptions}
                  required
                />
              </PermissionsGate>
            )}

            <FormSelect
              name="ownerId"
              label="Owner"
              placeholder="Select owner"
              options={ownerOptions}
              required
            />

            <UsersMultiSelect options={usersOptions} />

            {showSourcingStrategy && (
              <FormSelect
                name="sourcingStrategy"
                label="Sourcing Strategy"
                placeholder="Select a sourcing strategy"
                description={
                  !isCreateMode
                    ? 'Existing legacy or Scout jobs can switch only to Sourcing Agent and cannot switch back.'
                    : undefined
                }
                options={sourcingStrategyOptions}
                disabled={
                  isCreateMode
                    ? !isDevEnv && !isLocalEnv
                    : originalSourcingStrategy === 'Agent'
                }
                required
              />
            )}

            {isCreateMode && (
              <FormSwitch
                name="isPublicCandidateListEnabled"
                label="Public shortlist"
                description="Generate a shareable outbound shortlist page for sales prospects. You can change this later on the job edit page."
                checkedLabel="Enabled on create"
                uncheckedLabel="Disabled"
              />
            )}

            <PermissionsGate
              permissions={
                isCreateMode
                  ? [permissions.Jobs.Create]
                  : [permissions.Jobs.Update]
              }
            >
              <div className="flex justify-end gap-2">
                {onCancel && (
                  <Button variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" loading={loading}>
                  {submitText || 'Save'}
                </Button>
              </div>
            </PermissionsGate>
          </CardContent>
        </Card>

        {showOutreachAgentSettings && (
          <PermissionsGate permissions={[permissions.Messaging.Write]}>
            <Card>
              <CardHeader>
                <CardTitle>Agent settings</CardTitle>
                <CardDescription>
                  Configure how AI agents will interact with candidates
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-8">
                <FormRadioGroup
                  name="isMessagingAutomated"
                  label="How do you want to handle candidate communication?"
                  options={[
                    { value: 'false', label: 'Manual follow-up' },
                    { value: 'true', label: 'AI follow-up agent' },
                  ]}
                />

                <FormRadioGroup
                  name="isShortlistingAutomated"
                  label="Automatically shortlist sourced candidates?"
                  description="Move newly sourced candidates to the shortlist automatically when their assessment is ready."
                  options={[
                    {
                      value: 'true',
                      label: 'Yes, shortlist automatically',
                    },
                    { value: 'false', label: 'No, review manually' },
                  ]}
                />

                <FormRadioGroup
                  name="isReachOutAutomated"
                  label="Automated end-to-end sourcing to reachout "
                  options={[
                    { value: 'false', label: 'Shortlist only' },
                    { value: 'true', label: 'Automated reachout' },
                  ]}
                />

                <FormRadioGroup
                  name="isInvitationFollowUpEnabled"
                  label="Send a follow-up when a candidate accepts the invitation?"
                  options={[
                    { value: 'true', label: 'Yes, send follow-up' },
                    { value: 'false', label: 'No, never send follow-up' },
                  ]}
                />

                <FormRadioGroup
                  name="isPrescreeningEnabled"
                  label="How do you want to prescreen candidates?"
                  options={[
                    { value: 'false', label: 'Human prescreening' },
                    { value: 'true', label: 'AI prescreening agent' },
                  ]}
                />

                <FormRadioGroup
                  name="isMessagingCreative"
                  label="What reach out style do you prefer?"
                  options={[
                    { value: 'false', label: 'Message template' },
                    { value: 'true', label: 'Creative mode' },
                  ]}
                />

                <InitialReachOutMessageTemplate />

                <div className="flex justify-end gap-2">
                  {onCancel && (
                    <Button variant="outline" onClick={onCancel}>
                      Cancel
                    </Button>
                  )}
                  <Button type="submit" loading={loading}>
                    {submitText || 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </PermissionsGate>
        )}

        {isCoordinationAgentSettingsEnabled && (
          <PermissionsGate permissions={[permissions.Messaging.Write]}>
            <Card>
              <CardHeader>
                <CardTitle>Coordination Agent settings</CardTitle>
                <CardDescription>
                  Configure how the coordination agent schedules and manages
                  candidate meetings
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-8">
                <FormRadioGroup
                  name="isTrackingCoordinationEvents"
                  label="Do you want to track coordination for this job?"
                  description={
                    isCreateMode
                      ? undefined
                      : 'This can be changed until the first coordination process is created.'
                  }
                  disabled={hasCoordinationProcess}
                  options={[
                    { value: 'false', label: 'Not tracked' },
                    { value: 'true', label: 'Track coordination' },
                  ]}
                />

                <CoordinationActorFields
                  users={usersQuery.data ?? []}
                  hasCoordinationProcess={hasCoordinationProcess}
                  isCreateMode={isCreateMode}
                  singleTenantCompanyId={singleTenantCompanyId}
                />

                <p className="text-muted-foreground text-sm">
                  Coordination settings can be changed until the first
                  coordination process is created.
                </p>

                <FormRadioGroup
                  name="isCoordinationActionAutoApproved"
                  label="How should the coordination agent's suggested actions be approved?"
                  description={
                    isCreateMode
                      ? undefined
                      : 'This can be changed until the first coordination process is created.'
                  }
                  disabled={hasCoordinationProcess}
                  options={[
                    { value: 'false', label: 'Manual review' },
                    { value: 'true', label: 'Auto-approve' },
                  ]}
                />

                <div className="flex justify-end gap-2">
                  {onCancel && (
                    <Button variant="outline" onClick={onCancel}>
                      Cancel
                    </Button>
                  )}
                  <Button type="submit" loading={loading}>
                    {submitText || 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </PermissionsGate>
        )}
      </div>
    </Form>
  );
};

const InitialReachOutMessageTemplate = () => {
  const isMessagingCreative = useWatch({ name: 'isMessagingCreative' });

  return (
    <FormTextarea
      name="initialReachOutMessageTemplate"
      label="Initial Reach Out Message"
      placeholder="Hi John, your profile looks great. I'm currently looking for a senior software engineer with 3+ years of experience..."
      rows={6}
      disabled={isMessagingCreative === 'true'}
    />
  );
};

interface SingleTenantCompanyIdSyncProps {
  companyId?: string;
}

const SingleTenantCompanyIdSync = ({
  companyId,
}: SingleTenantCompanyIdSyncProps) => {
  const form = useFormContext<JobFormValues>();

  useEffect(() => {
    if (companyId && !form.getValues('companyId')) {
      form.setValue('companyId', companyId);
    }
  }, [companyId, form]);

  return null;
};

interface CoordinationActorFieldsProps {
  users: UserApi[];
  hasCoordinationProcess: boolean;
  isCreateMode: boolean;
  singleTenantCompanyId?: string;
}

const CoordinationActorFields = ({
  users,
  hasCoordinationProcess,
  isCreateMode,
  singleTenantCompanyId,
}: CoordinationActorFieldsProps) => {
  const form = useFormContext<JobFormValues>();
  const companyId = useWatch({ control: form.control, name: 'companyId' });
  const ownerId = useWatch({ control: form.control, name: 'ownerId' });
  const isTrackingCoordinationEvents = useWatch({
    control: form.control,
    name: 'isTrackingCoordinationEvents',
  });
  const recruiterId = useWatch({
    control: form.control,
    name: 'coordinationProcessSettings.recruiterId',
  });
  const hiringManagerId = useWatch({
    control: form.control,
    name: 'coordinationProcessSettings.hiringManagerId',
  });

  const effectiveCompanyId = companyId || singleTenantCompanyId;
  const coordinationUsers = effectiveCompanyId
    ? users.filter((user) =>
        (user.tenantIds ?? []).includes(effectiveCompanyId),
      )
    : [];
  const recruiterUsers = coordinationUsers.filter((user) =>
    (user.roles ?? []).includes(roles.Recruiter),
  );
  const hiringManagerUsers = coordinationUsers.filter((user) =>
    (user.roles ?? []).includes(roles.HiringManager),
  );
  const recruiterOptions = mapUsersToSelectOptions(recruiterUsers);
  const hiringManagerOptions = mapUsersToSelectOptions(hiringManagerUsers);

  useEffect(() => {
    if (hasCoordinationProcess) return;

    if (
      recruiterId &&
      !recruiterUsers.some((recruiter) => recruiter.id === recruiterId)
    ) {
      form.setValue('coordinationProcessSettings.recruiterId', '');
    }

    if (
      hiringManagerId &&
      !hiringManagerUsers.some(
        (hiringManager) => hiringManager.id === hiringManagerId,
      )
    ) {
      form.setValue('coordinationProcessSettings.hiringManagerId', '');
    }

    if (
      isCreateMode &&
      !recruiterId &&
      recruiterUsers.some((recruiter) => recruiter.id === ownerId)
    ) {
      form.setValue('coordinationProcessSettings.recruiterId', ownerId);
    }
  }, [
    form,
    hasCoordinationProcess,
    hiringManagerId,
    hiringManagerUsers,
    isCreateMode,
    ownerId,
    recruiterId,
    recruiterUsers,
  ]);

  const isTrackingCoordinationEnabled = isTrackingCoordinationEvents === 'true';

  useEffect(() => {
    if (!isTrackingCoordinationEnabled) {
      form.clearErrors([
        'coordinationProcessSettings.recruiterId',
        'coordinationProcessSettings.hiringManagerId',
      ]);
    }
  }, [form, isTrackingCoordinationEnabled]);

  return (
    <>
      <FormSelect
        name="coordinationProcessSettings.recruiterId"
        label="Coordination recruiter"
        placeholder="Select recruiter"
        options={recruiterOptions}
        disabled={hasCoordinationProcess}
        required={isTrackingCoordinationEnabled}
      />

      <FormSelect
        name="coordinationProcessSettings.hiringManagerId"
        label="Coordination hiring manager"
        placeholder="Select hiring manager"
        options={hiringManagerOptions}
        disabled={hasCoordinationProcess}
        required={isTrackingCoordinationEnabled}
      />
    </>
  );
};

const convertToString = (
  value: boolean | undefined,
  defaultValue = false,
): 'true' | 'false' => {
  if (value === undefined) return defaultValue ? 'true' : 'false';

  return value ? 'true' : 'false';
};
